import { useState } from 'react';
import { Plus, AlertTriangle, Calendar, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { EnhancedJournalFilters } from '../../components/journals/EnhancedJournalFilters';
import { JournalEntryView } from '../../components/journals/JournalEntryView';
import { NewJournalEntryModal } from '../../components/journals/NewJournalEntryModal';
import { JournalEntryEditModal } from '../../components/journals/JournalEntryEditModal';
import { AgentEntryEditModal } from '../../components/journals/AgentEntryEditModal';
import { UnifiedJournalList } from '../../components/journals/UnifiedJournalList';
import { Modal } from '../../components/ui/Modal';
import { BusinessUnitBanner } from '../../components/dashboard/BusinessUnitBanner';
import { useJournalEntries } from '../../hooks/useJournalEntries';
import { useAgentEntries } from '../../hooks/useAgentEntries';
import { useEnhancedJournalFilters } from '../../hooks/useEnhancedJournalFilters';
import { useOrganization } from '../../hooks/useOrganization';
import { useJournalExport } from '../../hooks/useJournalExport';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { usePermissions } from '../../hooks/usePermissions';
import { CloudinaryService } from '../../services/cloudinary/CloudinaryService';
import type { JournalEntry } from '../../types/accounting';

export function JournalsPage() {
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAgentEditModal, setShowAgentEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  
  const { currentFiscalYear } = useFiscalYearStore();
  
  // Permissions basées sur l'API /auth/me
  const { can } = usePermissions();
  const canCreateEntry = can('create_entries');
  const canValidateEntry = can('validate_entries');
  const canDeleteEntry = can('delete_entries');
  
  const { 
    entries, 
    loading, 
    pagination,
    addEntry, 
    updateEntry, 
    deleteEntry,
    submitEntry,
    approveEntry,
    rejectEntry,
    postEntry,
    validateAndPostEntry,
    getAvailableActions,
    loadEntries,
    setPage,
    setPageSize
  } = useJournalEntries();
  const { 
    entries: agentEntries, 
    loading: agentLoading, 
    validateEntry, 
    validateBatchEntries,
    updateEntry: updateAgentEntry,
    deleteEntry: deleteAgentEntry,
    loadEntries: loadAgentEntries
  } = useAgentEntries();

  // Fonction pour rafraîchir toutes les entrées (journal + agent)
  const handleRefresh = async () => {
    await Promise.all([loadEntries(), loadAgentEntries()]);
  };
  
  // Combiner toutes les entrées pour le filtrage avec protection renforcée
  const safeEntries = Array.isArray(entries) ? entries : [];
  const safeAgentEntries = Array.isArray(agentEntries) ? agentEntries : [];
  const allEntries = [...safeEntries, ...safeAgentEntries];
  
  // Vérification supplémentaire pour s'assurer qu'on a toujours un tableau
  if (!Array.isArray(allEntries)) {
    console.error('allEntries is not an array:', allEntries);
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">Erreur: Données invalides</div>
      </div>
    );
  }
  
  // Utiliser les nouveaux filtres améliorés
  const {
    filters,
    updateFilters,
    resetFilters,
    filteredEntries,
    isFiltersActive,
    filterStats,
    getJournalTypeOptions,
    getStatusOptions,
    getSourceOptions
  } = useEnhancedJournalFilters(allEntries);
  
  // Hook pour les informations d'organisation (export)
  const { organization } = useOrganization();

  // Hook pour l'export
  const {
    loading: isExporting,
    error: exportError,
    handleExport: exportJournal
  } = useJournalExport();

  const handleNewEntry = async (data: Partial<JournalEntry>) => {
    try {
      await addEntry(data);
      setShowNewEntryModal(false);
    } catch (error) {
      console.error('Failed to add journal entry:', error);
    }
  };

  const handleView = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const handleUploadAttachment = async (entry: JournalEntry, file: File) => {
    try {
      // Upload vers Cloudinary puis mise à jour de l'écriture avec l'URL
      const result = await CloudinaryService.uploadJournalAttachment(file);
      
      const newAttachment = {
        id: crypto.randomUUID(),
        name: file.name,
        url: result.url,
        mimeType: file.type,
        size: result.size,
        status: 'uploaded' as const
      };

      const updatedAttachments = [...(entry.attachments || []), newAttachment];
      await updateEntry(entry.id, { ...entry, attachments: updatedAttachments });
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      alert('Erreur lors de l\'upload de la pièce jointe. Veuillez réessayer.');
    }
  };

  const handleDownloadAttachment = async (entry: JournalEntry, attachmentId: string) => {
    try {
      const attachment = entry.attachments?.find(att => att.id === attachmentId);
      if (attachment?.url) {
        // Ouvrir l'URL Cloudinary dans un nouvel onglet pour téléchargement
        window.open(attachment.url, '_blank');
      } else if (attachment?.localUrl) {
        // Fallback vers l'URL locale (mode offline)
        const link = document.createElement('a');
        link.href = attachment.localUrl;
        link.download = attachment.name;
        link.click();
      }
    } catch (error) {
      console.error('Failed to download attachment:', error);
    }
  };

  const handleEdit = (entry: JournalEntry) => {
    // Les écritures peuvent être éditées si l'utilisateur a la permission de création
    // et que l'exercice est ouvert
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  const handleDelete = async (entry: JournalEntry) => {
    if (!canDeleteEntry) return;
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette écriture ?')) {
      try {
        // Distinguer entre les entrées agent et les entrées journal normales
        if (entry.source === 'agent') {
          await deleteAgentEntry(entry.id);
        } else {
          await deleteEntry(entry.id);
        }
      } catch (error) {
        console.error('Failed to delete entry:', error);
      }
    }
  };

  const handleUpdate = async (updatedEntry: JournalEntry) => {
    try {
      await updateEntry(updatedEntry.id, updatedEntry);
      setShowEditModal(false);
      setSelectedEntry(null);
    } catch (error) {
      console.error('Failed to update journal entry:', error);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      await exportJournal({
        entries: filteredEntries,
        format,
        filters,
        organization,
        title: 'JOURNAL DES ÉCRITURES COMPTABLES'
      });
    } catch (error) {
      console.error(`Erreur lors de l'export ${format}:`, error);
    }
  };

  // const handleAgentEntryEdit = (entry: JournalEntry) => {
  //   setSelectedEntry(entry);
  //   setShowAgentEditModal(true);
  // };

  const handleAgentEntryUpdate = async (updatedEntry: JournalEntry) => {
    try {
      await updateAgentEntry(updatedEntry.id, updatedEntry);
      setShowAgentEditModal(false);
      setSelectedEntry(null);
    } catch (error) {
      console.error('Failed to update agent entry:', error);
    }
  };

  // const handleAgentEntryDelete = async (entry: JournalEntry) => {
  //   if (window.confirm('Êtes-vous sûr de vouloir supprimer cette écriture générée par l\'agent ?')) {
  //     try {
  //       await deleteAgentEntry(entry.id);
  //     } catch (error) {
  //       console.error('Failed to delete agent entry:', error);
  //     }
  //   }
  // };

  const handleValidateEntry = async (entry: JournalEntry) => {
    if (!canValidateEntry) return;
    try {
      await validateEntry(entry.id);
    } catch (error) {
      console.error('Failed to validate entry:', error);
    }
  };

  const handleValidateBatchEntries = async (entries: JournalEntry[]) => {
    if (!canValidateEntry) return;
    try {
      const entryIds = entries.map(entry => entry.id);
      await validateBatchEntries(entryIds);
    } catch (error) {
      console.error('Failed to validate batch entries:', error);
    }
  };

  // const handleValidateBatchEntries = async (entryIds: string[]) => {
  //   try {
  //     await validateBatchEntries(entryIds);
  //   } catch (error) {
  //     console.error('Failed to validate batch entries:', error);
  //   }
  // };

  // Pagination côté serveur - les entrées renvoyées par le hook sont déjà paginées
  const normalEntries = filteredEntries.filter(entry => entry.source !== 'agent');
  const adhaEntries = filteredEntries.filter(entry => entry.source === 'agent');
  
  // Utiliser les bonnes entrées selon le filtre source
  const entriesToDisplay = filters.source === 'agent' ? adhaEntries : 
                           filters.source === 'manual' ? normalEntries : 
                           filteredEntries;
  
  // La pagination est gérée par le backend via le hook
  const currentPage = pagination.page;
  const totalPages = pagination.totalPages;
  const totalItems = pagination.total;
  
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
  };

  if (loading || agentLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bannière du périmètre de données (Entreprise/Succursale/Point de vente) */}
      <BusinessUnitBanner showChangeButton={false} />
      
      {/* Bandeau d'information sur l'exercice comptable */}
      {currentFiscalYear && (
        <Card className={`p-4 ${currentFiscalYear.status === 'closed' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'}`}>
          <div className="flex items-center gap-3">
            <Calendar className={`h-5 w-5 ${currentFiscalYear.status === 'closed' ? 'text-amber-600' : 'text-blue-600'}`} />
            <div className="flex-1">
              <span className="font-medium text-gray-900 dark:text-white">
                Exercice : {currentFiscalYear.code || 'N/A'}
              </span>
              <span className="text-gray-600 dark:text-gray-400 ml-2">
                ({new Date(currentFiscalYear.startDate).toLocaleDateString('fr-FR')} - {new Date(currentFiscalYear.endDate).toLocaleDateString('fr-FR')})
              </span>
            </div>
            {currentFiscalYear.status === 'closed' && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">Exercice clôturé - Lecture seule</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {!currentFiscalYear && (
        <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div>
              <span className="font-medium text-red-800 dark:text-red-200">
                Aucun exercice comptable sélectionné
              </span>
              <p className="text-sm text-red-600 dark:text-red-300">
                Veuillez sélectionner ou créer un exercice comptable dans les paramètres pour créer des écritures.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* En-tête avec actions */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Journal des Écritures
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gestion et consultation des écritures comptables
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={handleRefresh}
            disabled={loading || agentLoading}
            variant="outline"
            className="px-4 py-2 rounded-lg flex items-center gap-2"
            title="Rafraîchir la liste"
          >
            <RefreshCw className={`h-5 w-5 ${(loading || agentLoading) ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
          {canCreateEntry && (
            <Button 
              onClick={() => setShowNewEntryModal(true)}
              disabled={!currentFiscalYear || currentFiscalYear.status === 'closed'}
              className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-5 w-5" />
              Nouvelle Écriture
            </Button>
          )}
        </div>
      </div>

      {/* Filtres améliorés */}
      <EnhancedJournalFilters
        filters={filters}
        onFiltersChange={updateFilters}
        onReset={resetFilters}
        filterStats={filterStats}
        isFiltersActive={isFiltersActive}
        journalTypeOptions={getJournalTypeOptions}
        statusOptions={getStatusOptions}
        sourceOptions={getSourceOptions}
      />

      {/* Message d'erreur d'export */}
      {exportError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Erreur d'export: {exportError}
          </p>
        </div>
      )}

      {/* Liste unifiée des écritures */}
      <UnifiedJournalList
        entries={entriesToDisplay}
        agentEntries={agentEntries} 
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onValidate={handleValidateEntry}
        onValidateAll={handleValidateBatchEntries}
        onUploadAttachment={handleUploadAttachment}
        onDownloadAttachment={handleDownloadAttachment}
        onExport={handleExport}
        loading={isExporting}
        page={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Modales */}
      {showNewEntryModal && (
        <NewJournalEntryModal
          isOpen={showNewEntryModal}
          onClose={() => setShowNewEntryModal(false)}
          onSubmit={handleNewEntry}
        />
      )}

      {showViewModal && selectedEntry && (
        <Modal 
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title="Détails de l'écriture"
          size="xl"
          noPadding
        >
          <JournalEntryView
            entry={selectedEntry}
          />
        </Modal>
      )}

      {showEditModal && selectedEntry && (
        <JournalEntryEditModal
          entry={selectedEntry}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdate}
        />
      )}

      {showAgentEditModal && selectedEntry && (
        <AgentEntryEditModal
          entry={selectedEntry}
          isOpen={showAgentEditModal}
          onClose={() => setShowAgentEditModal(false)}
          onSave={handleAgentEntryUpdate}
          onValidate={handleValidateEntry}
        />
      )}
    </div>
  );
}
